import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { NetworkIcon } from '@/components/NetworkIcons';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';

export type CryptoNetwork = 
  | 'ethereum' 
  | 'polygon' 
  | 'solana' 
  | 'base' 
  | 'arbitrum' 
  | 'optimism' 
  | 'avalanche' 
  | 'tron' 
  | 'stellar';

export type CryptoAsset = 'USDC' | 'USDT' | 'USDB' | 'EURC' | 'DAI' | 'PYUSD' | 'USDP';

interface NetworkInfo {
  id: CryptoNetwork;
  name: string;
  avgTime: string;
  avgFee: string;
}

interface AssetInfo {
  id: CryptoAsset;
  name: string;
  networks: CryptoNetwork[];
}

// Network configurations
const NETWORKS: NetworkInfo[] = [
  { id: 'ethereum', name: 'Ethereum', avgTime: '5-15 min', avgFee: '$2-15' },
  { id: 'polygon', name: 'Polygon', avgTime: '2-5 min', avgFee: '<$0.01' },
  { id: 'solana', name: 'Solana', avgTime: '~1 min', avgFee: '<$0.01' },
  { id: 'base', name: 'Base', avgTime: '2-5 min', avgFee: '<$0.10' },
  { id: 'arbitrum', name: 'Arbitrum', avgTime: '2-10 min', avgFee: '<$0.25' },
  { id: 'optimism', name: 'Optimism', avgTime: '2-10 min', avgFee: '<$0.25' },
  { id: 'avalanche', name: 'Avalanche', avgTime: '2-5 min', avgFee: '<$0.10' },
  { id: 'tron', name: 'Tron', avgTime: '1-3 min', avgFee: '<$1.00' },
  { id: 'stellar', name: 'Stellar', avgTime: '~5 sec', avgFee: '<$0.01' },
];

// Asset configurations with supported networks
const ASSETS: AssetInfo[] = [
  { 
    id: 'USDC', 
    name: 'USD Coin',
    networks: ['ethereum', 'solana', 'base', 'arbitrum', 'optimism', 'polygon', 'avalanche', 'stellar']
  },
  { 
    id: 'USDT', 
    name: 'Tether',
    networks: ['ethereum', 'tron', 'polygon', 'arbitrum', 'optimism', 'avalanche', 'solana']
  },
  { 
    id: 'USDB', 
    name: 'USD Bridge',
    networks: ['base', 'solana']
  },
  { 
    id: 'EURC', 
    name: 'Euro Coin',
    networks: ['base', 'solana', 'ethereum']
  },
  { 
    id: 'DAI', 
    name: 'Dai',
    networks: ['ethereum', 'polygon', 'arbitrum', 'optimism']
  },
  { 
    id: 'PYUSD', 
    name: 'PayPal USD',
    networks: ['ethereum', 'solana']
  },
  { 
    id: 'USDP', 
    name: 'Pax Dollar',
    networks: ['ethereum', 'solana']
  },
];

interface NetworkSelectorProps {
  value: CryptoNetwork | null;
  onChange: (network: CryptoNetwork) => void;
  availableNetworks?: CryptoNetwork[];
  disabled?: boolean;
}

/**
 * NetworkSelector - Grid selector for blockchain networks
 */
export function NetworkSelector({
  value,
  onChange,
  availableNetworks,
  disabled = false,
}: NetworkSelectorProps) {
  const networks = availableNetworks 
    ? NETWORKS.filter(n => availableNetworks.includes(n.id))
    : NETWORKS;

  return (
    <div className="space-y-3">
      <Label className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
        Select Network
      </Label>
      
      <div className={cn(
        "grid grid-cols-2 sm:grid-cols-3 gap-2",
        disabled && "opacity-60 pointer-events-none"
      )}>
        {networks.map((network) => {
          const isSelected = value === network.id;
          
          return (
            <Card
              key={network.id}
              className={cn(
                "cursor-pointer min-h-[72px] relative",
                semanticTokens.motion.standard,
                semanticTokens.focus.ring,
                "hover:shadow-md",
                isSelected 
                  ? 'ring-2 ring-primary ring-offset-2 border-primary' 
                  : semanticTokens.border.default,
                semanticTokens.bg.surface,
                semanticTokens.shadow.sm,
                semanticTokens.radius.lg,
                "focus-visible:outline-none"
              )}
              onClick={() => onChange(network.id)}
              role="button"
              tabIndex={0}
              aria-label={`Select ${network.name} network`}
              aria-pressed={isSelected}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onChange(network.id);
                }
              }}
            >
              <CardContent className="p-3 flex flex-col items-center gap-2">
                <div className={cn(
                  "w-10 h-10 rounded-full",
                  "flex items-center justify-center",
                  isSelected ? "bg-primary/10" : semanticTokens.bg.muted
                )}>
                  <NetworkIcon network={network.id} className="w-6 h-6" />
                </div>
                <span className={cn(
                  semanticTokens.typography.label,
                  isSelected ? "text-primary" : semanticTokens.fg.default
                )}>
                  {network.name}
                </span>
                <div className={cn(
                  "flex flex-col items-center",
                  semanticTokens.typography.caption,
                  semanticTokens.fg.muted
                )}>
                  <span>{network.avgTime}</span>
                  <span>Fee: {network.avgFee}</span>
                </div>
                {isSelected && (
                  <SFIcon icon={sfIcons.success} className={cn("w-4 h-4 absolute top-2 right-2", semanticTokens.positive.accent)} />
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

interface AssetSelectorProps {
  value: CryptoAsset | null;
  onChange: (asset: CryptoAsset) => void;
  selectedNetwork?: CryptoNetwork | null;
  disabled?: boolean;
}

/**
 * AssetSelector - Horizontal scrollable asset selector
 */
export function AssetSelector({
  value,
  onChange,
  selectedNetwork,
  disabled = false,
}: AssetSelectorProps) {
  // Filter assets based on selected network
  const availableAssets = selectedNetwork
    ? ASSETS.filter(a => a.networks.includes(selectedNetwork))
    : ASSETS;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
          Select Asset
        </Label>
        {selectedNetwork && (
          <span className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
            on {NETWORKS.find(n => n.id === selectedNetwork)?.name}
          </span>
        )}
      </div>
      
      {availableAssets.length === 0 ? (
        <div className={cn(
          "flex items-center gap-2 p-3",
          semanticTokens.warning.bg,
          semanticTokens.warning.border,
          semanticTokens.radius.lg,
          "border"
        )}>
          <SFIcon icon={sfIcons.info} className={cn("w-4 h-4", semanticTokens.warning.accent)} />
          <span className={cn(semanticTokens.typography.body, semanticTokens.warning.fg)}>
            Select a network first to see available assets
          </span>
        </div>
      ) : (
        <div className={cn(
          "flex flex-wrap gap-2",
          disabled && "opacity-60 pointer-events-none"
        )}>
          {availableAssets.map((asset) => {
            const isSelected = value === asset.id;
            
            return (
              <button
                key={asset.id}
                onClick={() => onChange(asset.id)}
                disabled={disabled}
                className={cn(
                  "flex items-center gap-2",
                  "px-3 py-2.5 min-h-[52px]",
                  semanticTokens.radius.lg,
                  semanticTokens.motion.standard,
                  semanticTokens.focus.ring,
                  "border",
                  isSelected ? [
                    "border-primary bg-primary/5 dark:bg-primary/10",
                    "ring-2 ring-primary ring-offset-2",
                  ] : [
                    semanticTokens.border.default,
                    semanticTokens.bg.surface,
                    "hover:bg-muted/50",
                  ]
                )}
                aria-pressed={isSelected}
              >
                <CurrencyIcon symbol={asset.id} size="sm" />
                <div className="flex flex-col items-start">
                  <span className={cn(
                    semanticTokens.typography.label,
                    isSelected ? "text-primary" : semanticTokens.fg.default
                  )}>
                    {asset.id}
                  </span>
                  <span className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
                    {asset.name}
                  </span>
                </div>
                {isSelected && (
                  <SFIcon icon={sfIcons.success} className={cn("w-4 h-4 ml-auto", semanticTokens.positive.accent)} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface NetworkAssetSelectorProps {
  network: CryptoNetwork | null;
  asset: CryptoAsset | null;
  onNetworkChange: (network: CryptoNetwork) => void;
  onAssetChange: (asset: CryptoAsset) => void;
  disabled?: boolean;
}

/**
 * Combined Network + Asset selector
 */
export function NetworkAssetSelector({
  network,
  asset,
  onNetworkChange,
  onAssetChange,
  disabled = false,
}: NetworkAssetSelectorProps) {
  // When network changes, reset asset if not compatible
  const handleNetworkChange = (newNetwork: CryptoNetwork) => {
    onNetworkChange(newNetwork);
    
    // Check if current asset is compatible with new network
    if (asset) {
      const assetInfo = ASSETS.find(a => a.id === asset);
      if (assetInfo && !assetInfo.networks.includes(newNetwork)) {
        // Find first compatible asset
        const compatibleAsset = ASSETS.find(a => a.networks.includes(newNetwork));
        if (compatibleAsset) {
          onAssetChange(compatibleAsset.id);
        }
      }
    }
  };

  return (
    <div className="space-y-6">
      <NetworkSelector
        value={network}
        onChange={handleNetworkChange}
        disabled={disabled}
      />
      
      <AssetSelector
        value={asset}
        onChange={onAssetChange}
        selectedNetwork={network}
        disabled={disabled || !network}
      />

      {/* Network info tooltip */}
      {network && (
        <div className={cn(
          "flex items-start gap-3 p-3",
          semanticTokens.info.bg,
          semanticTokens.info.border,
          semanticTokens.radius.lg,
          "border"
        )}>
          <SFIcon icon={sfIcons.info} className={cn("w-4 h-4 shrink-0 mt-0.5", semanticTokens.info.accent)} />
          <div className="space-y-1">
            <p className={cn(semanticTokens.typography.label, semanticTokens.info.fg)}>
              {NETWORKS.find(n => n.id === network)?.name} Network
            </p>
            <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>
              Average confirmation: {NETWORKS.find(n => n.id === network)?.avgTime}
              {' · '}
              Network fee: {NETWORKS.find(n => n.id === network)?.avgFee}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper to get available networks for an asset
export function getNetworksForAsset(asset: CryptoAsset): CryptoNetwork[] {
  const assetInfo = ASSETS.find(a => a.id === asset);
  return assetInfo?.networks || [];
}

// Helper to get available assets for a network
export function getAssetsForNetwork(network: CryptoNetwork): CryptoAsset[] {
  return ASSETS.filter(a => a.networks.includes(network)).map(a => a.id);
}

// Helper to get network info
export function getNetworkInfo(network: CryptoNetwork): NetworkInfo | undefined {
  return NETWORKS.find(n => n.id === network);
}

